import Vue from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import ActionsCard from './ActionsCard/ActionsCard.vue'
import GigDetailsCard from './GigDetailsCard/GigDetailsCard.vue'
import GigJobDetailsCard from './GigJobDetailsCard/GigJobDetailsCard.vue'
import GigStatusBar from './GigStatusBar/GigStatusBar.vue'
import InformerCard from './InformerCard/InformerCard.vue'
import {
  applicationManageMixin,
  disputableMixin,
  offerManageMixin,
  payableMixin,
  refundableMixin,
  reviewableMixin,
  startableMixin,
  statusesMixin,
  waitForRefreshApplicationMixin,
} from './mixins'
import MobileActionsCard from './MobileActionsCard/MobileActionsCard.vue'
import OfferCard from './OfferCard/OfferCard.vue'
import { CHAT, SERVICE_DETAILS } from '@/constants/routes'
import responseMixin from '@/mixins/responseMixin'
import rolebleMixin from '@/mixins/rolebleMixin'
import unreadMessagesBlinkTitleMixin from '@/mixins/unreadMessagesBlinkTitleMixin'
import ChatRoom from '@/models-ts/chat/ChatRoom'
import Chat from '@/partials/ChatNew/Chat.vue'
import ChatRoomPrimaryInfo from '@/partials/ChatRoomPrimaryInfo/ChatRoomPrimaryInfo.vue'

export default Vue.extend<any, any, any, any>({
  mixins: [
    applicationManageMixin,
    disputableMixin,
    responseMixin,
    rolebleMixin,
    offerManageMixin,
    payableMixin,
    refundableMixin,
    reviewableMixin,
    startableMixin,
    statusesMixin,
    unreadMessagesBlinkTitleMixin,
    waitForRefreshApplicationMixin,
  ],
  components: {
    ActionsCard,
    MobileActionsCard,
    Chat,
    ChatRoomPrimaryInfo,
    GigDetailsCard,
    GigJobDetailsCard,
    GigStatusBar,
    InformerCard,
    OfferCard,
  },
  data() {
    return {
      initLoaded: false,
      withDetails: false,
      lastUpdatedRoomDate: null,
    }
  },
  computed: {
    ...mapState({
      userIsLoaded: (state: any) => state.user.profile.isLoaded,
      roomId: (state: any) => state.chatNew.openedRoomId,
      initialized: (state: any) => state.chatNew.initialized,
      application: (state: any) => state.gigChat.application.value,
    }),
    ...mapGetters({
      room: 'chatNew/getOpenedRoom',
    }),
    applicationId() {
      const { applicationId } = ChatRoom.parseRoomId(this.roomId)
      return applicationId
    },
    isFreelancerInfo() {
      return !this.application || !this.isFreelancer
    },
    hasOffer() {
      return this.initLoaded && !this.room?.temporary && this.application?.offer && !this.isStarted
    },
    roomUser() {
      if (this.application) {
        return this.isFreelancer ? this.application.customer : this.application.freelancer
      }
    },
    roomEntity() {
      if (!this.initLoaded) return
      if (this.application) {
        return {
          name: this.application.gig.name,
          link: {
            name: SERVICE_DETAILS,
            params: {
              id: this.application.gig.id,
              slug: this.application.gig.slug,
            },
          },
        }
      }
    },
    backLink() {
      return this.roomEntity?.link
    },
  },
  watch: {
    initialized: {
      handler() {
        if (this.initialized) {
          if (!this.roomId) {
            const room = this.$route.query.room
            if (room) {
              this.openRoom(room)
            } else {
              this.$router.replace({ name: CHAT })
            }
          }
        }
      },
      immediate: true,
    },
    roomId: {
      async handler() {
        if (this.roomId) {
          this.initLoaded = false
          await this.getApplication(this.applicationId)
          this.initLoaded = true
        }
        if (this.room && this.roomId !== this.$route.query.room) {
          this.$router.replace(this.room.chatLink)
        }
        if (this.$route.query.action) {
          this.processInitialAction(this.$route.query.action)
        }
      },
      immediate: true,
    },
    'room.updatedAt': {
      handler() {
        if (this.lastUpdatedRoomDate) {
          let dateA = Number(new Date(this.lastUpdatedRoomDate))
          let dateB = Number(new Date(this.room.updatedAt))
          if (Math.abs(dateA - dateB) > 1000) {
            console.log('Room updated, refreshing application...')
            this.waitRefreshApplication()
          }
        }
        this.lastUpdatedRoomDate = this.room?.updatedAt
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.closeRoom()
    this.resetGigChatState()
  },
  methods: {
    ...mapActions({
      openRoom: 'chatNew/openRoom',
      closeRoom: 'chatNew/closeRoom',
      getApplication: 'gigChat/getApplication',
    }),
    ...mapMutations({
      updateApplication: 'gigChat/updateApplication',
      resetGigChatState: 'gigChat/resetState',
    }),
    onDeclineGigByFreelancerClick() {
      this.declineApplicationByFreelancer()
    },
    onDeclineGigByCustomerClick() {
      this.declineApplicationByCustomer()
    },
    onOfferCancel(id: number) {
      if (this.application.offer?.id === id) {
        this.updateApplication({ id: this.application.id, offer: null })
      }
    },
    onOfferEdit() {
      this.editOffer()
    },
    async onOfferAccepted() {
      await this.waitRefreshApplication()
      this.startJob()
    },
    onMessageSent() {
      this.markApplicationAsRead()
    },
    processInitialAction(action: string) {
      switch (action) {
        case 'pay':
          return this.initialPayJob()
        case 'dispute':
          return this.initialDispute()
        case 'refund':
          return this.initialRefund()
        case 'feedback':
          return this.initialLeaveFeedback()
      }
    },
    initialPayJob() {
      if (this.hasPayJob || this.hasAcceptJob) {
        this.payToTalent()
      }
    },
    initialDispute() {
      if (this.hasCreateDispute) {
        this.createDispute()
      }
    },
    initialRefund() {
      if (this.hasRefund) {
        this.refundToCustomer()
      }
    },
    initialLeaveFeedback() {
      if (this.hasLeaveReview) {
        this.leaveReview()
      }
    },
  },
  metaInfo() {
    return {
      title: this.getMetaTitle(this.application?.gig?.name || 'Chat'),
    }
  },
})
